import api from './api';
import { User } from '../types/user';
import { Flight } from '../types/flight';
import { FlightParticipant } from '../types/flightParticipant';

export const getUserFlights = async (userId: string): Promise<FlightParticipant[]> => {
  const response = await api.get(`/users/${userId}/flights`);
  return response.data;
};

export const getUserCompletedJumps = async (userId: string): Promise<FlightParticipant[]> => {
  const response = await api.get(`/users/${userId}/flights`, { params: { completedOnly: 'true' } });
  return response.data;
};

export const getPilotFlights = async (userId: string): Promise<Flight[]> => {
  const response = await api.get(`/users/${userId}/pilot-flights`);
  return response.data;
};

export const getJumpHistoryPdf = async (userId: string): Promise<{ blob: Blob; filename: string }> => {
  const response = await api.get(`/users/${userId}/jump-history-pdf`, { responseType: 'blob' });
  const contentDisposition = response.headers['content-disposition'];
  const filename = contentDisposition
    ? contentDisposition.split('filename=')[1]?.replace(/"/g, '') || `JumpHistory.pdf`
    : `JumpHistory.pdf`;
  return { blob: response.data, filename };
};

export const getPilotHistoryPdf = async (userId: string): Promise<{ blob: Blob; filename: string }> => {
  const response = await api.get(`/users/${userId}/pilot-history-pdf`, { responseType: 'blob' });
  const contentDisposition = response.headers['content-disposition'];
  const filename = contentDisposition
    ? contentDisposition.split('filename=')[1]?.replace(/"/g, '') || `PilotHistory.pdf`
    : `PilotHistory.pdf`;
  return { blob: response.data, filename };
};

export const getAllUsers = async (
  page: number,
  pageSize: number,
  sortBy: string,
  order: 'asc' | 'desc',
  searchTerm: string,
  role?: string,
  tenantId?: string
): Promise<{ users: User[], total: number }> => {
  const params: any = { page, pageSize, sortBy, order, searchTerm };
  if (role) {
    params.role = role;
  }
  if (tenantId) {
    params.tenantId = tenantId;
  }
  const response = await api.get('/users', {
    params,
  });
  return {
    users: response.data.users,
    total: response.data.total,
  };
};

export const searchUsers = async (searchTerm: string, roles?: string[]): Promise<User[]> => {
  const params: { q: string; role?: string } = { q: searchTerm };
  if (roles && roles.length > 0) {
    params.role = roles.join(',');
  }
  const response = await api.get(`/users/search`, { params });
  return response.data;
};

export const getUserById = async (id: string): Promise<User> => {
  const response = await api.get(`/users/${id}`);
  return response.data;
};

type UserCreatePayload = Partial<Omit<User, 'id' | 'tenants' | 'password'>> & {
  password?: string;
  tenants?: { tenantId: string, role: string }[];
};

export const createUser = async (userData: UserCreatePayload): Promise<User> => {
  const response = await api.post('/users', userData);
  return response.data;
};

type UserUpdatePayload = Partial<Omit<User, 'id' | 'tenants' | 'password'>> & {
  tenants?: { tenantId: string, role: string }[];
};

export const updateUser = async (userId: string, updates: UserUpdatePayload): Promise<User> => {
  const response = await api.put(`/users/${userId}`, updates);
  return response.data;
};

export const deleteUser = async (userId: string): Promise<void> => {
  await api.delete(`/users/${userId}`);
};

export const resetUserPassword = async (
  userId: string,
  opts?: { newPassword?: string; sendEmail?: boolean }
): Promise<{ emailSent?: boolean }> => {
  const response = await api.post(`/users/${userId}/reset-password`, opts ?? {});
  return response.data ?? {};
};

/** Conferma a mano l'email di una persona (admin o operatore del tenant). */
export const verifyUserEmail = async (userId: string): Promise<void> => {
  await api.post(`/users/${userId}/verify-email`);
};

export const changePassword = async (userId: string, currentPassword: string, newPassword: string): Promise<void> => {
  await api.put(`/users/${userId}/change-password`, { currentPassword, newPassword });
};

export const uploadUserPhoto = async (userId: string, file: Blob): Promise<User> => {
  const formData = new FormData();
  formData.append('photo', file, 'photo.jpg');
  const response = await api.post(`/users/${userId}/upload-photo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};
/** Se l'utente riceve le email che annunciano i nuovi eventi della scuola corrente. */
export const getEventEmailsPreference = async (): Promise<{ optedOut: boolean }> => {
  const response = await api.get('/users/me/event-emails');
  return response.data;
};

export const setEventEmailsPreference = async (optedOut: boolean): Promise<{ optedOut: boolean }> => {
  const response = await api.put('/users/me/event-emails', { optedOut });
  return response.data;
};
